import { FC, useEffect, useState } from "react";

import { DonationFormCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { trpc } from "../../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../../common/LiveFormEditorContext";
import { useBuildPrefilledDescription } from "../../../common/prefillDataBuilders/useBuildPrefilledDescription";
import { useUpdateDonationDataInPreview } from "../../../common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useFormSaveAndNavigate } from "../../useFormSaveAndNavigate";

import { useDescriptionGenerationPolling } from "../StepDescriptionPoweredByAI/useDescriptionGenerationPolling";

import { LiveEditorStepDescriptionAiWizardStateless } from "./LiveEditorStepDescriptionAiWizard.stateless";
import { DescriptionWizardMode } from "./ModeTabs/ModeTabs";
import { useDescriptionWizardSession } from "./helpers/useDescriptionWizardSession";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import {
  getDescriptionWizardWorkedOnKey,
  getFromLocalStorage,
  removeFromLocalStorage,
  setInLocalStorage,
} from "@/common/helpers/localStorage";
import { normalizeHtml } from "@/components/OutlinedTextFieldWysiwyg/useOutlinedTextFieldWysiwyg";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { StepDescriptionFormData } from "@/features/LiveFormEditor/Steps/StepDescription/shared/types";

export const DonationStepDescriptionAiWizard: FC = () => {
  const {
    apiFormObject: apiDonationForm,
    formCategory,
    optionalFundraiserToken,
    isCreatedFromDuplicate,
  } = useLiveFormEditorContext<"donation-form">();
  const { t } = useTranslate();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const localizedDonationFormField = apiDonationForm.donationFormFields?.find(
    (field) => field.locale === apiDonationForm.locale
  );
  const initialDescription = localizedDonationFormField?.description || null;

  const { buildPrefilledDescription } = useBuildPrefilledDescription();
  const prefilledText = buildPrefilledDescription({
    organizationName: apiDonationForm.organization?.name || "",
    isPeerToPeerCampaign: false,
  });

  const workedOnStorageKey = getDescriptionWizardWorkedOnKey(apiDonationForm.id);
  const form = useForm<StepDescriptionFormData>({
    defaultValues: {
      description: initialDescription,
      hasWorkedOnDescription: getFromLocalStorage?.(workedOnStorageKey) === "true",
    },
  });
  const description = form.watch("description");
  const hasWorkedOnDescription = form.watch("hasWorkedOnDescription");

  const [mode, setMode] = useState<DescriptionWizardMode>("generate");
  const [promptValue, setPromptValue] = useState("");
  const [repromptValue, setRepromptValue] = useState("");

  const [manualPrefill, setManualPrefill] = useState<{ status: "loading" | "resolved"; description: string | null }>({
    status: initialDescription ? "resolved" : "loading",
    description: null,
  });

  const { pollDescriptionGeneration } = useDescriptionGenerationPolling(apiDonationForm.id);

  useEffect(() => {
    if (initialDescription) {
      return;
    }

    let mounted = true;

    const fetchBackgroundDescription = async () => {
      const { descriptionStatus, description } = await pollDescriptionGeneration({ minimumAnimationMs: 0 });
      if (!mounted) {
        return;
      }
      setManualPrefill({
        status: "resolved",
        description: descriptionStatus === "completed" ? description : null,
      });
    };

    void fetchBackgroundDescription();

    return () => {
      mounted = false;
    };
  }, [apiDonationForm.id, initialDescription, pollDescriptionGeneration]);

  const wizardTitle = localizedDonationFormField?.title || apiDonationForm.organization?.name || "Donation form";

  const { phase, session, generate, regenerate, reset } = useDescriptionWizardSession({
    formId: apiDonationForm.id,
    title: wizardTitle,
    locale: apiDonationForm.locale,
    onDescriptionGenerated: (description) => {
      form.setValue("description", description, { shouldDirty: true, shouldTouch: true });
      form.setValue("hasWorkedOnDescription", true, { shouldDirty: true });
      setRepromptValue("");
    },
  });

  const handleGenerate = () => {
    logUserActionEvent(AmplitudeEvents.AiDescriptionWizardGenerateClicked);
    void generate(promptValue.trim());
  };

  const handleRegenerate = () => {
    logUserActionEvent(AmplitudeEvents.AiDescriptionWizardRegenerateClicked);
    void regenerate(repromptValue.trim(), form.getValues("description"));
  };

  const handleModeChange = (nextMode: DescriptionWizardMode) => {
    if (nextMode === mode) {
      return;
    }
    logUserActionEvent(AmplitudeEvents.AiDescriptionWizardTabSwitched, { tab: nextMode });
    setMode(nextMode);
  };

  const isManualEditorEmpty = !form.watch("description")?.trim?.();
  const isManualPrefillLoading = mode === "manual" && isManualEditorEmpty && manualPrefill.status === "loading";

  useEffect(() => {
    if (mode !== "manual" || manualPrefill.status !== "resolved") {
      return;
    }
    if (!form.getValues("description")?.trim?.()) {
      form.setValue("description", manualPrefill.description ?? prefilledText, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [mode, manualPrefill, form, prefilledText]);

  const handleDeleteDescription = () => {
    form.setValue("description", null, { shouldDirty: true });
    form.setValue("hasWorkedOnDescription", false, { shouldDirty: true });
    setMode("generate");
    setPromptValue("");
    setRepromptValue("");
    reset();
  };

  const { mutateAsync: updateDescription } = trpc.updateDonationFormDescription.useMutation();
  const startThankYouEmailGenerationWithAIMutation = trpc.startThankYouEmailGenerationWithAI.useMutation();

  const saveData = async (formData: StepDescriptionFormData) => {
    const result = await updateDescription({
      donationFormId: apiDonationForm.id,
      description: formData.description,
      fundraiserToken: optionalFundraiserToken,
      aiSessionId: session && formData.description ? session.aiSessionId : undefined,
    });
    if (!result.error && result.data) {
      if (formData.hasWorkedOnDescription) {
        setInLocalStorage?.(workedOnStorageKey, "true");
      } else {
        removeFromLocalStorage?.(workedOnStorageKey);
      }
    }
    return result;
  };

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepDescriptionFormData>({
    type: "donation-form",
    form,
    saveData,
    onFirstNextCompleted: () => {
      if (isCreatedFromDuplicate) {
        return;
      }

      void startThankYouEmailGenerationWithAIMutation.mutateAsync({
        formId: apiDonationForm.id,
        title: localizedDonationFormField?.title || "",
        description: form.getValues("description"),
        campaignDate: null,
        campaignLocation: null,
        formCategory,
        locale: apiDonationForm.locale,
      });
    },
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const descriptionInPreview = formData.description ?? prefilledText;
      liveDataToMutate.donation.donationFormFields![0].description = descriptionInPreview;
      liveDataToMutate.donation.donationFormFields![0].sanitizedDescription = descriptionInPreview;
      liveDataToMutate.placeholders.isDescription = !formData.description;
    },
  });

  const stepTitle =
    apiDonationForm.category === DonationFormCategory.PeerToPeerCampaign
      ? t("dashboard", "liveFormEditor.stepDescription.p2p.title")
      : t("dashboard", "liveFormEditor.stepDescription.donationForm.title");

  return (
    <LiveEditorStepDescriptionAiWizardStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      mode={mode}
      phase={phase === "generating" ? phase : hasWorkedOnDescription ? "generated" : "idle"}
      isManualPrefillLoading={isManualPrefillLoading}
      promptValue={promptValue}
      repromptValue={repromptValue}
      onModeChange={handleModeChange}
      onPromptChange={setPromptValue}
      onRepromptChange={setRepromptValue}
      onGenerate={handleGenerate}
      onRegenerate={handleRegenerate}
      onDeleteDescription={handleDeleteDescription}
      onDescriptionChange={(value) => {
        if (normalizeHtml(value) !== normalizeHtml(description ?? "")) {
          form.setValue("hasWorkedOnDescription", true, { shouldDirty: true });
        }
      }}
      wording={{
        stepTitle,
        stepDescription: t("dashboard", "liveFormEditor.descriptionWizard.stepDescription"),
      }}
    />
  );
};
